"use client"

import { Fragment, useMemo } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"

import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { Button, clx } from "@medusajs/ui"
import { XMark } from "@medusajs/icons"

import useToggleState from "@lib/hooks/use-toggle-state"
import {
  VARIANT_FILTER_KEYS,
  type VariantFilterFacets,
  type VariantFilterKey,
  type VariantFilterState,
  parseVariantFilterStateFromSearchParams,
} from "@lib/util/variant-filters"

import SortProducts, { SortOptions } from "./sort-products"

type CategoryOption = {
  id: string
  name: string
  slug?: string
  handle?: string
}

type RefinementListProps = {
  sortBy: SortOptions
  search?: boolean
  categories?: CategoryOption[]
  variantFilterOptions?: VariantFilterFacets
  "data-testid"?: string
}

const FILTER_GROUP_LABELS: Record<VariantFilterKey, string> = {
  talle: "Talle",
  color: "Color",
  material: "Material",
}

const normalizeForCompare = (value: string) => value.trim().toLowerCase()

const RefinementList = ({
  sortBy,
  categories,
  variantFilterOptions,
  "data-testid": dataTestId,
}: RefinementListProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const filterDrawer = useToggleState(false)

  const selectedVariantFilters = useMemo(
    () => parseVariantFilterStateFromSearchParams(searchParams),
    [searchParams]
  )

  const availableVariantFilters = useMemo<VariantFilterFacets>(() => {
    return {
      talle: Array.isArray(variantFilterOptions?.talle)
        ? variantFilterOptions.talle
        : [],
      color: Array.isArray(variantFilterOptions?.color)
        ? variantFilterOptions.color
        : [],
      material: Array.isArray(variantFilterOptions?.material)
        ? variantFilterOptions.material
        : [],
    }
  }, [variantFilterOptions])

  const hasVariantFilterOptions = VARIANT_FILTER_KEYS.some(
    (key) => availableVariantFilters[key].length > 0
  )

  const activeFilterCount = VARIANT_FILTER_KEYS.reduce(
    (acc, key) => acc + selectedVariantFilters[key].length,
    0
  )

  const updateSingleQueryParam = (name: string, value?: string | null) => {
    const params = new URLSearchParams(searchParams)

    if (!value) {
      params.delete(name)
    } else {
      params.set(name, value)
    }

    params.delete("page")

    const queryString = params.toString()
    router.push(queryString ? `${pathname}?${queryString}` : pathname)
  }

  const updateMultiQueryParam = (name: VariantFilterKey, values: string[]) => {
    const params = new URLSearchParams(searchParams)
    params.delete(name)

    values.forEach((value) => {
      const trimmed = value.trim()
      if (trimmed.length > 0) {
        params.append(name, trimmed)
      }
    })

    params.delete("page")

    const queryString = params.toString()
    router.push(queryString ? `${pathname}?${queryString}` : pathname)
  }

  const toggleVariantFilterValue = (
    filterKey: VariantFilterKey,
    value: string
  ) => {
    const currentValues = selectedVariantFilters[filterKey]
    const target = normalizeForCompare(value)

    const exists = currentValues.some(
      (entry) => normalizeForCompare(entry) === target
    )
    const nextValues = exists
      ? currentValues.filter((entry) => normalizeForCompare(entry) !== target)
      : [...currentValues, value]

    updateMultiQueryParam(filterKey, nextValues)
  }

  const categoryLinks = Array.isArray(categories)
    ? categories
        .filter(
          (category) => category?.name && (category?.slug || category?.handle)
        )
        .map((category) => ({
          name: category.name,
          path: `/categories/${category.slug || category.handle}`,
          id: category.id,
        }))
    : []

  const hasCategories = categoryLinks.length > 0

  return (
    <div className="w-full small:w-auto">
      <div className="hidden small:flex small:flex-col gap-12 py-4 mb-8 small:px-0 small:min-w-[250px] small:ml-[1.675rem]">
        <SortProducts
          sortBy={sortBy}
          setQueryParam={updateSingleQueryParam}
          data-testid={dataTestId}
        />

        {hasVariantFilterOptions ? (
          <VariantFilterGroups
            availableVariantFilters={availableVariantFilters}
            selectedVariantFilters={selectedVariantFilters}
            onToggle={toggleVariantFilterValue}
          />
        ) : null}

        {hasCategories && (
          <div className="flex flex-col gap-3">
            <p className="txt-compact-small-plus text-ui-fg-muted">
              Explorar categorías
            </p>
            <ul className="flex flex-col gap-2">
              {categoryLinks.map((category) => (
                <li key={category.id}>
                  <LocalizedClientLink
                    href={category.path}
                    className="txt-compact-small text-ui-fg-subtle hover:text-ui-fg-base"
                  >
                    {category.name}
                  </LocalizedClientLink>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="small:hidden w-full">
        <div className="flex items-center justify-between px-6 py-4">
          {hasCategories ? (
            <p className="txt-compact-small-plus text-ui-fg-muted">
              Explorar categorías
            </p>
          ) : (
            <span />
          )}
          <Button
            variant="primary"
            size="small"
            onClick={filterDrawer.open}
            data-testid="mobile-filter-button"
            className="border-none rounded-xs shadow-none px-4 py-2 font-lealti-light text-white bg-black"
          >
            {activeFilterCount > 0 ? `Filtrar (${activeFilterCount})` : "Filtrar"}
          </Button>
        </div>

        {hasCategories && (
          <div className="overflow-x-auto px-6 pb-8 pt-4">
            <ul className="flex gap-3">
              {categoryLinks.map((category) => (
                <li key={category.id}>
                  <LocalizedClientLink
                    href={category.path}
                    className="txt-compact-small text-ui-fg-subtle hover:text-ui-fg-base  whitespace-nowrap"
                  >
                    {category.name}
                  </LocalizedClientLink>
                </li>
              ))}
            </ul>
          </div>
        )}

        <MobileFilterDrawer
          isOpen={filterDrawer.state}
          onClose={filterDrawer.close}
          sortBy={sortBy}
          updateSingleQueryParam={(name, value) => {
            updateSingleQueryParam(name, value)
            filterDrawer.close()
          }}
          onToggleVariantFilter={toggleVariantFilterValue}
          categoryLinks={categoryLinks}
          hasCategories={hasCategories}
          dataTestId={dataTestId}
          availableVariantFilters={availableVariantFilters}
          selectedVariantFilters={selectedVariantFilters}
        />
      </div>
    </div>
  )
}

export default RefinementList

type CategoryLink = {
  id: string
  name: string
  path: string
}

type MobileFilterDrawerProps = {
  isOpen: boolean
  onClose: () => void
  sortBy: SortOptions
  updateSingleQueryParam: (name: string, value?: string) => void
  onToggleVariantFilter: (filterKey: VariantFilterKey, value: string) => void
  categoryLinks: CategoryLink[]
  hasCategories: boolean
  dataTestId?: string
  availableVariantFilters: VariantFilterFacets
  selectedVariantFilters: VariantFilterState
}

const MobileFilterDrawer = ({
  isOpen,
  onClose,
  sortBy,
  updateSingleQueryParam,
  onToggleVariantFilter,
  categoryLinks,
  hasCategories,
  dataTestId,
  availableVariantFilters,
  selectedVariantFilters,
}: MobileFilterDrawerProps) => {
  const hasVariantFilterOptions = VARIANT_FILTER_KEYS.some(
    (key) => availableVariantFilters[key].length > 0
  )

  const activeFilterCount = VARIANT_FILTER_KEYS.reduce(
    (acc, key) => acc + selectedVariantFilters[key].length,
    0
  )

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[75]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-x-0 bottom-0 z-10">
          <Transition.Child
            as={Fragment}
            enter="transform transition ease-out duration-300"
            enterFrom="translate-y-full"
            enterTo="translate-y-0"
            leave="transform transition ease-in duration-200"
            leaveFrom="translate-y-0"
            leaveTo="translate-y-full"
          >
            <Dialog.Panel className="bg-white rounded-t-2xl shadow-2xl flex flex-col max-h-[88vh]">
              {/* Drag handle */}
              <div className="flex justify-center pt-3 pb-1 shrink-0">
                <div className="w-10 h-1 rounded-full bg-gray-200" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-6 py-3.5 border-b border-gray-100 shrink-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900">Filtros</p>
                  {activeFilterCount > 0 && (
                    <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-semibold text-white bg-black rounded-full leading-none">
                      {activeFilterCount}
                    </span>
                  )}
                </div>
                <button
                  onClick={onClose}
                  className="w-9 h-9 rounded-full border border-gray-200 inline-flex items-center justify-center leading-none text-gray-400 hover:text-gray-700 hover:border-gray-400 transition-colors duration-150 shrink-0"
                  aria-label="Cerrar filtros"
                >
                  <XMark className="w-4 h-4 shrink-0" />
                </button>
              </div>

              {/* Scrollable content */}
              <div className="flex-1 overflow-y-auto overscroll-contain">
                {/* Sort */}
                <div className="px-6 py-5">
                  <SortProducts
                    sortBy={sortBy}
                    setQueryParam={updateSingleQueryParam}
                    data-testid={dataTestId}
                  />
                </div>

                {/* Variant filter groups */}
                {hasVariantFilterOptions && (
                  <>
                    <div className="h-px bg-gray-100 mx-6" />
                    <div className="px-6 py-5">
                      <VariantFilterGroups
                        availableVariantFilters={availableVariantFilters}
                        selectedVariantFilters={selectedVariantFilters}
                        onToggle={onToggleVariantFilter}
                      />
                    </div>
                  </>
                )}

                {/* Categories as pill chips */}
                {hasCategories && (
                  <>
                    <div className="h-px bg-gray-100 mx-6" />
                    <div className="px-6 py-5">
                      <p className="txt-compact-small-plus text-ui-fg-muted mb-3">
                        Explorar categorías
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {categoryLinks.map((category) => (
                          <LocalizedClientLink
                            key={category.id}
                            href={category.path}
                            onClick={onClose}
                            className="inline-flex items-center px-3.5 py-1.5 rounded-full border border-gray-200 text-xs text-gray-600 hover:border-gray-900 hover:text-gray-900 transition-colors duration-150"
                          >
                            {category.name}
                          </LocalizedClientLink>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <div className="h-8" />
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}

type VariantFilterGroupsProps = {
  availableVariantFilters: VariantFilterFacets
  selectedVariantFilters: VariantFilterState
  onToggle: (filterKey: VariantFilterKey, value: string) => void
}

const VariantFilterGroups = ({
  availableVariantFilters,
  selectedVariantFilters,
  onToggle,
}: VariantFilterGroupsProps) => {
  return (
    <div className="flex flex-col gap-6">
      {VARIANT_FILTER_KEYS.map((filterKey) => {
        const options = availableVariantFilters[filterKey]
        if (!options.length) {
          return null
        }

        const selectedValues = selectedVariantFilters[filterKey]

        return (
          <div key={filterKey} className="flex flex-col gap-3">
            <p className="txt-compact-small-plus text-ui-fg-muted">
              {FILTER_GROUP_LABELS[filterKey]}
            </p>
            <ul className="flex flex-col gap-0.5">
              {options.map((value) => {
                const isSelected = selectedValues.some(
                  (selected) =>
                    normalizeForCompare(selected) === normalizeForCompare(value)
                )

                const inputId = `filter-${filterKey}-${normalizeForCompare(value).replace(/\s+/g, "-")}`

                return (
                  <li key={`${filterKey}-${value}`}>
                    <label
                      htmlFor={inputId}
                      className="flex items-center gap-3 cursor-pointer group py-1.5"
                    >
                      <input
                        id={inputId}
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggle(filterKey, value)}
                        className="sr-only"
                      />
                      <div
                        className={clx(
                          "w-4.5 h-4.5 shrink-0 rounded-[3px] border flex items-center justify-center transition-all duration-150",
                          isSelected
                            ? "bg-black border-black"
                            : "bg-white border-gray-300 group-hover:border-gray-500"
                        )}
                      >
                        {isSelected && (
                          <svg
                            className="w-2.5 h-2.5 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={3}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </div>
                      <span
                        className={clx(
                          "txt-compact-small transition-colors duration-150",
                          isSelected
                            ? "text-ui-fg-base"
                            : "text-ui-fg-subtle group-hover:text-ui-fg-base"
                        )}
                      >
                        {value}
                      </span>
                    </label>
                  </li>
                )
              })}
            </ul>
          </div>
        )
      })}
    </div>
  )
}
